/* ═══════════════════════════════════════════════════════════════
   ProjectSpecService — Per-project spec forks via EC2 API (cloud, in-memory cache)

   Two parallel tables:
     - project_specs         : the published / approved state (the "main branch")
     - project_spec_drafts   : per-user working copies (the "feature branches")

   Flow mirrors SpecMasterService — user checks out a draft, edits it, submits
   for review, a publisher approves, approval copies the draft back onto the
   ProjectSpec, clears the draft, and appends a new Issuance snapshot.
   ═══════════════════════════════════════════════════════════════ */

import type {
  ProjectSpec,
  ProjectSpecDraft,
  Issuance,
  IssuanceSnapshot,
  PublishRequest,
} from '../../shared/types';
import * as cache from './cloudCache';
import type { TableOpts } from './cloudCache';
import * as cloudApi from './apiClient';
import { getCurrentFirmId } from './firmContext';
import { log as logActivity } from './activityLogger';

const opts: TableOpts = {
  jsonFields: [
    'coreContent',
    'systems',
    'variants',
    'issuances',
    'revisionSettings',
    'syncBase',
    'publishedContent',
    'publishedSystems',
  ],
};

const draftOpts: TableOpts = {
  jsonFields: ['coreContent', 'variants', 'publishRequest'],
};

export const ProjectSpecService = {
  /** List all project specs for the firm */
  list: async (): Promise<ProjectSpec[]> => cache.list<ProjectSpec>('project_specs', opts),

  /** List specs for a specific project */
  listByProject: async (projectId: string): Promise<ProjectSpec[]> => {
    const all = await ProjectSpecService.list();
    return all.filter((ps) => ps.projectId === projectId);
  },

  /** Get a single project spec */
  get: async (id: string): Promise<ProjectSpec | null> => {
    const all = await ProjectSpecService.list();
    return all.find((ps) => ps.id === id) || null;
  },

  /** Save (create or update) a project spec */
  save: async (spec: ProjectSpec, o?: { collabMirror?: boolean }): Promise<ProjectSpec> =>
    // `collabMirror`: the Designer's own JSON mirror write (the master-draft mirror sets the same flag).
    cache.save('project_specs', o?.collabMirror ? { ...spec, collabMirror: true } : spec, opts),

  /** Delete a project spec */
  delete: async (id: string): Promise<void> => cache.remove('project_specs', id),

  /** Archive or restore — a THREE-key partial update on purpose: the server
   *  reads an `archived_at` flip of three keys or fewer as an archive toggle
   *  and gates it on `project_specs.archive`. Mirrors ModuleService.setArchived
   *  — project_specs stamps its editor via `edited_by` (not `modified_by`),
   *  so that's what's sent; `last_edited` is left alone to stay within the
   *  server's 3-key threshold (migration 050 added archived_at/archived_by). */
  setArchived: async (id: string, archived: boolean, userName?: string): Promise<void> => {
    const firmId = getCurrentFirmId();
    await cloudApi.dbUpdate(
      'project_specs',
      {
        archived_at: archived ? new Date().toISOString() : null,
        archived_by: archived ? userName || null : null,
        edited_by: userName || null,
      },
      [
        { col: 'firm_id', op: '=', val: firmId },
        { col: 'id', op: '=', val: id },
      ]
    );
    cache.invalidate('project_specs');
    logActivity({
      action: archived ? 'project_spec.archive' : 'project_spec.restore',
      entityType: 'project_spec',
      entityId: id,
    });
  },

  // ─── Issuances (frozen snapshots — the change-tracking baseline) ───

  /** Issue the spec: publish `content` onto the ProjectSpec row and append an
   *  Issuance carrying a full frozen snapshot. That snapshot becomes the new
   *  baseline the revision-mark engine diffs against. */
  createIssuance: async (
    spec: ProjectSpec,
    content: {
      coreContent: any[];
      systems: any[];
      variants?: any[];
      /** What the frozen snapshot holds when it differs from the live row
       *  (the Publish gate: an issue freezes the PUBLISHED view). */
      snapshot?: { coreContent: any[]; systems: any[] };
    },
    meta: { name: string; date?: string; notes?: string; issuedBy?: string }
  ): Promise<ProjectSpec> => {
    const now = new Date().toISOString();
    const snapshot: IssuanceSnapshot = JSON.parse(
      JSON.stringify({
        coreContent: (content.snapshot ?? content).coreContent || [],
        systems: (content.snapshot ?? content).systems || [],
        tokenOverrides: spec.tokenOverrides,
        formaId: spec.formaId,
        csiNumber: spec.csiNumber,
        title: spec.title,
      })
    );
    const issuance: Issuance = {
      id: 'iss-' + Date.now().toString(36) + Math.random().toString(36).substr(2, 4),
      name: meta.name,
      date: meta.date || now,
      notes: meta.notes,
      createdAt: now,
      issuedBy: meta.issuedBy,
      snapshot,
    };
    const next: ProjectSpec = {
      ...spec,
      coreContent: content.coreContent,
      ...(content.variants ? { variants: content.variants } : {}),
      issuances: [...(spec.issuances || []), issuance],
      lastEdited: now,
      editedBy: meta.issuedBy,
    };
    return cache.save('project_specs', next, opts);
  },

  /** Revert the working copy to an issuance's snapshot. The revert itself is
   *  recorded as a new issuance (carrying a copy of the restored snapshot) so
   *  history stays traceable and revision marks re-baseline to the restored
   *  state. Caller is responsible for the permission gate + confirm UI. */
  revertToIssuance: async (
    spec: ProjectSpec,
    issuanceId: string,
    userName?: string
  ): Promise<ProjectSpec> => {
    const target = (spec.issuances || []).find((i) => i.id === issuanceId);
    if (!target?.snapshot) throw new Error('That issuance has no snapshot to restore');
    const now = new Date().toISOString();
    const snap: IssuanceSnapshot = JSON.parse(JSON.stringify(target.snapshot));
    const revertIssuance: Issuance = {
      id: 'iss-' + Date.now().toString(36) + Math.random().toString(36).substr(2, 4),
      name: `Reverted to ${target.name}`,
      date: now,
      createdAt: now,
      issuedBy: userName,
      revertedFromId: target.id,
      snapshot: snap,
    };
    const next: ProjectSpec = {
      ...spec,
      coreContent: snap.coreContent,
      systems: snap.systems,
      ...(snap.tokenOverrides !== undefined ? { tokenOverrides: snap.tokenOverrides } : {}),
      ...(snap.formaId !== undefined ? { formaId: snap.formaId } : {}),
      issuances: [...(spec.issuances || []), revertIssuance],
      lastEdited: now,
      editedBy: userName,
    };
    return cache.save('project_specs', next, opts);
  },

  // ─── Drafts (working copies) ───

  /** List every draft the firm has ever created. */
  listDrafts: async (): Promise<ProjectSpecDraft[]> =>
    cache.list<ProjectSpecDraft>('project_spec_drafts', draftOpts),

  /** Get the current user's active draft of a given project spec, if any. */
  getDraftForUser: async (
    projectSpecId: string,
    userName: string
  ): Promise<ProjectSpecDraft | null> => {
    const all = await ProjectSpecService.listDrafts();
    return all.find((d) => d.projectSpecId === projectSpecId && d.editedBy === userName) || null;
  },

  /** Check out a project spec as a draft. If one already exists for the user,
   *  returns it. Otherwise forks a new draft from the current project spec state. */
  checkout: async (projectSpec: ProjectSpec, userName: string): Promise<ProjectSpecDraft> => {
    const existing = await ProjectSpecService.getDraftForUser(projectSpec.id, userName);
    if (existing) return existing;

    const now = new Date().toISOString();
    const latestIssuance = (projectSpec.issuances || []).slice(-1)[0];
    const draft: ProjectSpecDraft = {
      id: 'psd-' + Date.now().toString(36) + Math.random().toString(36).substr(2, 4),
      projectSpecId: projectSpec.id,
      baseIssuanceId: latestIssuance?.id,
      editedBy: userName,
      csiNumber: projectSpec.csiNumber,
      title: projectSpec.title,
      description: projectSpec.description,
      coreContent: JSON.parse(JSON.stringify(projectSpec.coreContent || [])),
      variants: JSON.parse(JSON.stringify(projectSpec.variants || [])),
      createdAt: now,
      lastSaved: now,
    };
    return cache.save('project_spec_drafts', draft, draftOpts);
  },

  /** Save progress on a draft (every save stamps lastSaved). */
  saveDraft: async (draft: ProjectSpecDraft): Promise<ProjectSpecDraft> => {
    const updated: ProjectSpecDraft = { ...draft, lastSaved: new Date().toISOString() };
    return cache.save('project_spec_drafts', updated, draftOpts);
  },

  /** Abandon a draft — throw away all changes. */
  discardDraft: async (draftId: string): Promise<void> =>
    cache.remove('project_spec_drafts', draftId),

  /** Submit the draft for a publisher's review. Creates a PublishRequest. */
  submitForReview: async (draft: ProjectSpecDraft, message?: string): Promise<ProjectSpecDraft> => {
    const now = new Date().toISOString();
    const request: PublishRequest = {
      id: 'psr-' + Date.now().toString(36) + Math.random().toString(36).substr(2, 4),
      draftId: draft.id,
      blueprintId: draft.projectSpecId, // field named blueprintId for serialization parity with SpecMasterDraft
      requestedBy: draft.editedBy,
      requestedAt: now,
      message,
      status: 'pending',
    };
    const updated: ProjectSpecDraft = { ...draft, publishRequest: request, lastSaved: now };
    return cache.save('project_spec_drafts', updated, draftOpts);
  },

  /** Approve a draft — copy its content onto the ProjectSpec, append a new
   *  issuance snapshot, and clear the draft. `newIssuanceName` is required
   *  (e.g. "IFC", "Addendum 1"). */
  approveDraft: async (
    draft: ProjectSpecDraft,
    reviewer: string,
    newIssuanceName: string,
    reviewNote?: string
  ): Promise<ProjectSpec> => {
    const currentSpec = await ProjectSpecService.get(draft.projectSpecId);
    if (!currentSpec) throw new Error('Project spec no longer exists');
    const now = new Date().toISOString();
    const newIssuance: Issuance = {
      id: 'iss-' + Date.now().toString(36) + Math.random().toString(36).substr(2, 4),
      name: newIssuanceName,
      date: now,
      notes: reviewNote,
      createdAt: now,
      issuedBy: reviewer,
      // Freeze the approved content — this issuance becomes the new
      // revision-mark baseline, same as a direct createIssuance.
      snapshot: JSON.parse(
        JSON.stringify({
          coreContent: draft.coreContent || [],
          systems: currentSpec.systems || [],
          tokenOverrides: currentSpec.tokenOverrides,
          formaId: currentSpec.formaId,
          csiNumber: draft.csiNumber ?? currentSpec.csiNumber,
          title: draft.title ?? currentSpec.title,
        })
      ),
    };
    const nextSpec: ProjectSpec = {
      ...currentSpec,
      csiNumber: draft.csiNumber ?? currentSpec.csiNumber,
      title: draft.title ?? currentSpec.title,
      description: draft.description ?? currentSpec.description,
      coreContent: draft.coreContent,
      variants: draft.variants,
      issuances: [...(currentSpec.issuances || []), newIssuance],
      lastEdited: now,
      editedBy: reviewer,
    };
    const saved = await ProjectSpecService.save(nextSpec);
    await ProjectSpecService.discardDraft(draft.id);
    return saved;
  },

  /** Reject a pending draft — leave the draft in place so the author can revise,
   *  but mark the publish request as rejected with the reviewer's note. */
  rejectDraft: async (
    draft: ProjectSpecDraft,
    reviewer: string,
    reviewNote?: string
  ): Promise<ProjectSpecDraft> => {
    if (!draft.publishRequest) throw new Error('Draft has no pending review');
    const now = new Date().toISOString();
    const updated: ProjectSpecDraft = {
      ...draft,
      publishRequest: {
        ...draft.publishRequest,
        status: 'rejected',
        reviewedBy: reviewer,
        reviewedAt: now,
        reviewNote,
      },
      lastSaved: now,
    };
    return cache.save('project_spec_drafts', updated, draftOpts);
  },
};
